import React, { useState, useEffect, useMemo } from 'react';
import { Users, Edit } from 'lucide-react';
import { GenericListView, FilterConfig, StatusPill, getStatusPillClassName, PatientAvatar } from '../common';
import { fetchPatients } from '../../services/admin/patient.service';
import { useCanPerform } from '../../hooks/useCanPerform';

// Local interface for UI representation
interface Patient {
  id: string;
  mrn: string;
  name: string;
  dob: string;
  gender: string;
  email: string;
  phone: string; // Maps to phoneNumber in API
  status: 'PENDING_ADMISSION' | 'ACTIVE' | 'INACTIVE' | 'ON_HOLD';
  profilePictureUrl?: string | null;
  familyPortalEnabled?: boolean;
  // familyPortalUserStatus removed from Patient type
}

interface PatientAdminProps {
  expanded?: boolean;
  toggleExpanded?: () => void;
  onNavigate?: (action: 'add' | 'edit', patientId?: string) => void;
}

const PatientAdmin: React.FC<PatientAdminProps> = ({ expanded = true, toggleExpanded, onNavigate = () => {} }) => {
  // Check if user can create patients (requires admin:patients:phi:create permission)
  const canCreatePatients = useCanPerform('create:patients:phi');

  // Helper function to format dates consistently as MM/DD/YY
  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Helper functions to map gender between UI and API formats
  const mapGenderToUi = (apiGenderCode: string): string => {
    switch (apiGenderCode?.toUpperCase()) {
      case 'M': return 'Male';
      case 'F': return 'Female';
      case 'O': return 'Other';
      default: return apiGenderCode || '';
    }
  };
  
  // State for patients
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING_ADMISSION' | 'ACTIVE' | 'INACTIVE' | 'ON_HOLD'>('ALL');

  // Fetch patients from API
  useEffect(() => {
    const loadPatients = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await fetchPatients(
          currentPage, 
          pageSize, 
          searchTerm, 
          statusFilter !== 'ALL' ? statusFilter : undefined
        );
        
        // Map API response to our UI model
        const apiPatients: Patient[] = response.patients.map(patient => ({
          id: patient.id,
          mrn: patient.mrn,
          name: `${patient.firstName} ${patient.lastName}`,
          dob: patient.dob,
          gender: patient.gender,
          email: patient.email,
          phone: patient.phoneNumber,
          status: patient.status,
          profilePictureUrl: patient.profilePictureUrl || null,
          familyPortalEnabled: patient.familyPortalEnabled,
          // familyPortalUserStatus removed from API mapping
        })).sort((a, b) => (a.status === 'ACTIVE' ? 0 : a.status === 'ON_HOLD' ? 1 : 2) - (b.status === 'ACTIVE' ? 0 : b.status === 'ON_HOLD' ? 1 : 2));

        // Update pagination data
        setTotalPages(response.pagination.totalPages || Math.ceil(response.totalCount / pageSize));
        setTotalCount(response.totalCount);
        
        setPatients(apiPatients);
        setFilteredPatients(apiPatients);
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Failed to load patients:', error);
        setError('Failed to load patients. Please try again later.');
        setPatients([]);
        setFilteredPatients([]);
      } finally {
        setIsLoading(false);
        setRefreshing(false);
      }
    };

    loadPatients();
  }, [statusFilter, currentPage, pageSize, searchTerm]);

  // Function to handle filtering
  useEffect(() => {
    let result = [...patients];
    if (searchTerm) {
      result = result.filter(patient =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.phone.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'ALL') {
      result = result.filter(patient => patient.status === statusFilter);
    }
    setFilteredPatients(result);
  }, [searchTerm, patients, statusFilter]);

  // Add Patient - Navigate to add page
  const handleAddPatient = () => {
    onNavigate('add');
  };

  // Edit Patient - Navigate to edit page
  const handleEditPatient = (patient: Patient) => {
    onNavigate('edit', patient.id);
  };

  // Function to refresh the list from API
  const refreshList = async () => {
    setRefreshing(true);
    setError(null);

    try {
      const response = await fetchPatients(
        currentPage,
        pageSize,
        searchTerm,
        statusFilter !== 'ALL' ? statusFilter : undefined
      );

      // Map API response to our UI model
      const apiPatients: Patient[] = response.patients.map(patient => ({
        id: patient.id,
        mrn: patient.mrn,
        name: `${patient.firstName} ${patient.lastName}`,
        dob: patient.dob,
        gender: patient.gender,
        email: patient.email,
        phone: patient.phoneNumber,
        status: patient.status,
        profilePictureUrl: patient.profilePictureUrl || null,
        familyPortalEnabled: patient.familyPortalEnabled,
        // familyPortalUserStatus removed from API mapping
      })).sort((a, b) => (a.status === 'ACTIVE' ? 0 : a.status === 'ON_HOLD' ? 1 : 2) - (b.status === 'ACTIVE' ? 0 : b.status === 'ON_HOLD' ? 1 : 2));

      // Update pagination data
      setTotalPages(response.pagination.totalPages || Math.ceil(response.totalCount / pageSize));
      setTotalCount(response.totalCount);

      setPatients(apiPatients);
      setFilteredPatients(apiPatients);
    } catch (error) {
      console.error('Failed to refresh patients:', error);
      setError('Failed to refresh patients. Please try again later.');
    } finally {
      setRefreshing(false);
    }
  };
  
  // Build filter configurations for GenericListView
  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      value: statusFilter,
      options: [
        { value: 'ALL', label: 'All Statuses' },
        { value: 'ACTIVE', label: 'Active' },
        { value: 'ON_HOLD', label: 'On Hold' },
        { value: 'INACTIVE', label: 'Inactive' },
      ],
      onChange: (value) => setStatusFilter(value as 'ALL' | 'PENDING_ADMISSION' | 'ACTIVE' | 'INACTIVE' | 'ON_HOLD'),
    },
  ], [statusFilter]);

  // Status badge color now handled by StatusPill component

  return (
    <GenericListView
      // ListView props
      title="Patient Administration"
      icon={<Users size={20} />}
      expanded={expanded}
      onToggleExpand={toggleExpanded}
      hideHeaderOnDesktop
      // Data props
      items={filteredPatients}
      keyExtractor={(patient) => patient.id}
      columns={[
        {
          key: 'patient',
          label: 'Patient',
          width: 'flex-1 min-w-0',
          render: (patient: Patient) => (
            <div className="flex items-center gap-3">
              {/* Patient avatar - show image if available, initials as fallback */}
              <PatientAvatar
                patientId={patient.id}
                name={patient.name}
                profilePictureUrl={patient.profilePictureUrl}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {patient.name}
                </div>
                <div className="text-xs text-gray-500 truncate">MRN: {patient.mrn}</div>
              </div>
            </div>
          ),
        },
        {
          key: 'dob',
          label: 'DOB',
          width: 'w-24',
          align: 'center' as const,
          render: (patient: Patient) => (
            <span className="text-sm text-gray-500">{formatDate(patient.dob)}</span>
          ),
        },
        {
          key: 'gender',
          label: 'Gender',
          width: 'w-20',
          align: 'center' as const,
          render: (patient: Patient) => (
            <span className="text-sm text-gray-500">{mapGenderToUi(patient.gender)}</span>
          ),
        },
        {
          key: 'phone',
          label: 'Phone',
          width: 'w-32',
          render: (patient: Patient) => (
            <span className="text-sm text-gray-500">{patient.phone}</span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          width: 'w-24',
          align: 'center' as const,
          render: (patient: Patient) => (
            <StatusPill status={patient.status} />
          ),
        },
      ]}
      actions={[
        {
          icon: <Edit size={14} />,
          label: 'Edit patient',
          onClick: (patient: Patient) => handleEditPatient(patient),
          className: 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors',
        },
      ]}
      // Mobile card config
      mobileCard={{
        avatar: (patient: Patient) => (
          <PatientAvatar
            patientId={patient.id}
            name={patient.name}
            profilePictureUrl={patient.profilePictureUrl}
            size="lg"
          />
        ),
        primaryText: (patient: Patient) => patient.name,
        secondaryText: (patient: Patient) => `${formatDate(patient.dob)} • ${mapGenderToUi(patient.gender)} • MRN: ${patient.mrn}`,
        tertiaryText: (patient: Patient) => patient.phone || '',
        badges: (patient: Patient) => [
          {
            label: patient.status,
            className: getStatusPillClassName(patient.status),
          },
        ],
        actions: (patient: Patient) => [
          {
            icon: <Edit size={18} />,
            label: 'Edit',
            onClick: () => handleEditPatient(patient),
          },
        ],
      }}
      // Action bar props
      onRefresh={refreshList}
      refreshing={refreshing}
      loading={isLoading}
      showNew={canCreatePatients}
      newButtonLabel="Add Patient"
      onNewClick={handleAddPatient}
      // Pagination props
      pagination={{
        currentPage,
        totalPages,
        pageSize,
        totalCount,
        onPageChange: setCurrentPage,
      }}
      // State props
      error={error}
      onDismissError={() => setError(null)}
      hasLoadedOnce={hasLoadedOnce}
      // Empty state props
      emptyStateIcon={<Users size={48} className="text-gray-300" />}
      emptyStateTitle="No patients found"
      emptyStateMessage={searchTerm || statusFilter !== 'ALL'
        ? 'Try adjusting your filters or search term.'
        : 'Get started by adding a new patient.'}
      showList={true}
      // Search and filter props
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search patients by name, email, or phone..."
      filters={filterConfigs}
      resultCount={filteredPatients.length}
      filterStorageKey="patientAdmin"
      desktopInlineFilters
      desktopFiltersCollapsedByDefault
      hideDesktopFilterResultCount
    />
  );
};

export default PatientAdmin;
